import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { 
  Clock, 
  UserX, 
  UserCheck, 
  MessageSquare, 
  Trash2, 
  CheckCircle, 
  AlertCircle,
  Flag 
} from 'lucide-react';
import api from '../api/axios';

const ModerationHistory = () => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, reports, bans

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await api.get('/moderation/history');
      setActions(response.data);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (type) => {
    switch (type) {
      case 'note_added':
        return <MessageSquare className="w-5 h-5 text-indigo-500" />;
      case 'status_change':
        return <CheckCircle className="w-5 h-5 text-blue-500" />;
      case 'offer_deleted':
        return <Trash2 className="w-5 h-5 text-red-500" />;
      case 'user_ban':
        return <UserX className="w-5 h-5 text-orange-500" />;
      case 'user_unban':
        return <UserCheck className="w-5 h-5 text-green-500" />;
      case 'report_created':
        return <Flag className="w-5 h-5 text-yellow-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  const getActionColor = (type) => {
    switch (type) {
      case 'note_added':
        return 'bg-indigo-100 text-indigo-700';
      case 'status_change':
        return 'bg-blue-100 text-blue-700';
      case 'offer_deleted':
        return 'bg-red-100 text-red-700';
      case 'user_ban':
        return 'bg-orange-100 text-orange-700';
      case 'user_unban':
        return 'bg-green-100 text-green-700';
      case 'report_created':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getActionLabel = (type) => {
    switch (type) {
      case 'note_added':
        return 'Note ajoutée';
      case 'status_change':
        return 'Statut changé';
      case 'offer_deleted':
        return 'Offre supprimée';
      case 'user_ban':
        return 'Utilisateur banni';
      case 'user_unban':
        return 'Utilisateur débanni';
      case 'report_created':
        return 'Signalement créé';
      default:
        return 'Action inconnue';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const filteredActions = actions.filter(action => {
    if (filter === 'reports') {
      return ['note_added', 'status_change', 'report_created', 'offer_deleted'].includes(action.type);
    }
    if (filter === 'bans') {
      return ['user_ban', 'user_unban'].includes(action.type);
    }
    return true;
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-gray-500 text-center">Chargement de l'historique...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl">Historique des actions de modération</CardTitle>
            <div className="flex gap-2">
              <Badge
                variant={filter === 'all' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilter('all')}
              >
                Tout ({actions.length})
              </Badge>
              <Badge
                variant={filter === 'reports' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilter('reports')}
              >
                Signalements
              </Badge>
              <Badge
                variant={filter === 'bans' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilter('bans')}
              >
                Bannissements
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredActions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Aucune action pour ce filtre</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredActions.map((action, index) => (
                <div
                  key={index}
                  className="flex gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow">
                      {getActionIcon(action.type)}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={getActionColor(action.type)}>
                        {getActionLabel(action.type)}
                      </Badge>
                      <span className="text-sm text-gray-500">
                        {formatDate(action.created_at || action.at)}
                      </span>
                    </div>

                    {/* Moderator */}
                    <div className="flex items-center gap-2 mb-2">
                      <Avatar className="w-6 h-6">
                        <AvatarFallback className="text-xs bg-purple-100 text-purple-700">
                          {action.by_name?.charAt(0) || action.moderator_name?.charAt(0) || 'M'}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium text-gray-700">
                        {action.by_name || action.moderator_name || 'Modérateur'}
                      </span>
                    </div>

                    {/* Details */}
                    <div className="text-sm text-gray-600">
                      {action.type === 'note_added' && (
                        <p className="italic">&quot;{action.note}&quot;</p>
                      )}
                      {action.type === 'status_change' && action.from && action.to && (
                        <p>De <strong>{action.from}</strong> à <strong>{action.to}</strong></p>
                      )}
                      {action.type === 'user_ban' && (
                        <div>
                          <p>
                            Utilisateur: <strong>{action.target_user_name}</strong>
                          </p>
                          <p>Durée: <strong>{action.duration_days} jours</strong></p>
                          {action.reason && <p className="italic">Raison: &quot;{action.reason}&quot;</p>}
                        </div>
                      )}
                      {action.type === 'user_unban' && (
                        <p>
                          Utilisateur: <strong>{action.target_user_name}</strong>
                        </p>
                      )}
                      {action.type === 'offer_deleted' && action.offer_title && (
                        <p>Offre: <strong>{action.offer_title}</strong></p>
                      )}
                      {action.type === 'report_created' && (
                        <div>
                          {action.offer_title && <p>Offre: <strong>{action.offer_title}</strong></p>}
                          {action.reason && <p className="italic">Raison: &quot;{action.reason}&quot;</p>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ModerationHistory;
